import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosInstance from "../config/Axios";
import { useHoldGuard } from "../schedule/holdGuard";
import { useCustomToast } from "../pages/Contexts/ToastContext";

// ─── Fetch all hardware for company ────────────────────────────────────────
const getHardwareList = async ({ queryKey }) => {
    const [, params] = queryKey;
    const { data } = await axiosInstance.get("/hardware", { params });
    return data;
};

export const useGetHardwareList = (params = {}) => {
    const { showToast } = useCustomToast();
    return useQuery(["hardwareList", params], getHardwareList, {
        keepPreviousData: true,
        onError: (err) => showToast("error", "Hardware", err.response?.data?.message),
    });
};

// ─── Fetch single hardware device ───────────────────────────────────────────
const getHardwareById = async ({ queryKey }) => {
    const [, id] = queryKey;
    const { data } = await axiosInstance.get(`/hardware/${id}`);
    return data;
};

export const useGetHardware = (id) => {
    const { showToast } = useCustomToast();
    return useQuery(["hardware", id], getHardwareById, {
        enabled: !!id,
        onError: (err) => showToast("error", "Hardware", err.response?.data?.message),
    });
};

// ─── Create hardware ────────────────────────────────────────────────────────
export const useCreateHardware = () => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    return useMutation((payload) => axiosInstance.post("/hardware/create", payload), {
        onSuccess: () => {
            qc.invalidateQueries("hardwareList");
            // Also bust the role-filtered cache — DevicesPage parent picker,
            // ReceiversPage encoder/iTach, ScreensPage decoder, Remote Control
            // + Scenes PTN builders all read from useGetHardwareByRole and
            // otherwise stay stale after a create.
            qc.invalidateQueries("hardwareByRole");
            showToast("success", "Hardware", "Device created successfully!");
        },
        onError: (err) => showToast("error", "Hardware", err.response?.data?.message),
    });
};

// ─── Update hardware ────────────────────────────────────────────────────────
export const useUpdateHardware = (id) => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    return useMutation((payload) => axiosInstance.put(`/hardware/${id}/update`, payload), {
        onSuccess: () => {
            qc.invalidateQueries("hardwareList");
            qc.invalidateQueries("hardwareByRole");
            qc.invalidateQueries(["hardware", id]);
            showToast("success", "Hardware", "Device updated!");
        },
        onError: (err) => showToast("error", "Hardware", err.response?.data?.message),
    });
};

// ─── Delete hardware ────────────────────────────────────────────────────────
export const useDeleteHardware = () => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    return useMutation((id) => axiosInstance.delete(`/hardware/${id}/delete`), {
        onSuccess: () => {
            qc.invalidateQueries("hardwareList");
            qc.invalidateQueries("hardwareByRole");
            showToast("success", "Hardware", "Device removed!");
        },
        onError: (err) => showToast("error", "Hardware", err.response?.data?.message),
    });
};

// ─── Send preset command ────────────────────────────────────────────────────
export const useSendCommand = (hardwareId) => {
    const { showToast } = useCustomToast();
    return useMutation(
        (commandId) => axiosInstance.post(`/hardware/${hardwareId}/command/send`, { command_id: commandId }),
        {
            onSuccess: () => showToast("success", "Hardware", "Command sent!"),
            onError: (err) => showToast("error", "Hardware", err.response?.data?.message),
        }
    );
};

// ─── Create preset command ──────────────────────────────────────────────────
export const useCreateCommand = (hardwareId) => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    return useMutation(
        (payload) => axiosInstance.post(`/hardware/${hardwareId}/command/create`, payload),
        {
            onSuccess: () => {
                qc.invalidateQueries(["hardware", hardwareId]);
                showToast("success", "Hardware", "Command saved!");
            },
            onError: (err) => showToast("error", "Hardware", err.response?.data?.message),
        }
    );
};

// ─── Matrix routing ─────────────────────────────────────────────────────────
export const useMatrixRoute = (hardwareId) => {
    const { showToast } = useCustomToast();
    return useMutation(
        ({ input, output }) => axiosInstance.post(`/hardware/${hardwareId}/matrix/route`, { input, output }),
        {
            onSuccess: (res) => showToast("success", "Matrix", `Routed input ${res.data?.command ?? ""}`),
            onError: (err) => showToast("error", "Matrix", err.response?.data?.message),
        }
    );
};

export const useMatrixGetRouting = (hardwareId) => {
    const { showToast } = useCustomToast();
    return useQuery(
        ["matrixRouting", hardwareId],
        () => axiosInstance.get(`/hardware/${hardwareId}/matrix/routing`).then((r) => r.data),
        {
            enabled: !!hardwareId,
            onError: (err) => showToast("error", "Matrix", err.response?.data?.message),
        }
    );
};

export const useMatrixPower = (hardwareId) => {
    const { showToast } = useCustomToast();
    return useMutation(
        (on) => axiosInstance.post(`/hardware/${hardwareId}/matrix/power`, { on }),
        {
            onSuccess: () => showToast("success", "Matrix", "Power command sent!"),
            onError: (err) => showToast("error", "Matrix", err.response?.data?.message),
        }
    );
};

// ─── iTach IR ───────────────────────────────────────────────────────────────
export const useITachSendIR = (hardwareId) => {
    const { showToast } = useCustomToast();
    return useMutation(
        (payload) => axiosInstance.post(`/hardware/${hardwareId}/itach/ir`, payload),
        {
            onSuccess: () => showToast("success", "iTach", "IR signal sent!"),
            onError: (err) => showToast("error", "iTach", err.response?.data?.message),
        }
    );
};

export const useITachStopIR = (hardwareId) => {
    const { showToast } = useCustomToast();
    return useMutation(
        (payload) => axiosInstance.post(`/hardware/${hardwareId}/itach/stop`, payload),
        {
            onSuccess: () => showToast("success", "iTach", "IR stopped!"),
            onError: (err) => showToast("error", "iTach", err.response?.data?.message),
        }
    );
};

// ─── Denon Audio ─────────────────────────────────────────────────────────────
export const useAudioPower = (hardwareId) => {
    const { showToast } = useCustomToast();
    return useMutation(
        (on) => axiosInstance.post(`/hardware/${hardwareId}/audio/power`, { on }),
        {
            onSuccess: () => showToast("success", "Audio", "Power command sent!"),
            onError: (err) => showToast("error", "Audio", err.response?.data?.message),
        }
    );
};

export const useAudioVolume = (hardwareId) => {
    const { showToast } = useCustomToast();
    return useMutation(
        (level) => axiosInstance.post(`/hardware/${hardwareId}/audio/volume`, { level }),
        {
            onSuccess: () => showToast("success", "Audio", "Volume set!"),
            onError: (err) => showToast("error", "Audio", err.response?.data?.message),
        }
    );
};

export const useAudioVolumeStep = (hardwareId) => {
    const { showToast } = useCustomToast();
    return useMutation(
        (direction) => axiosInstance.post(`/hardware/${hardwareId}/audio/volume/step`, { direction }),
        {
            onError: (err) => showToast("error", "Audio", err.response?.data?.message),
        }
    );
};

export const useAudioMute = (hardwareId) => {
    const { showToast } = useCustomToast();
    return useMutation(
        (mute) => axiosInstance.post(`/hardware/${hardwareId}/audio/mute`, { mute }),
        {
            // react-query passes (data, variables) — second arg is the mutation input.
            onSuccess: (_data, mute) => showToast("success", "Audio", mute ? "Muted!" : "Unmuted!"),
            onError: (err) => showToast("error", "Audio", err.response?.data?.message),
        }
    );
};

export const useAudioInput = (hardwareId) => {
    const { showToast } = useCustomToast();
    return useMutation(
        (input) => axiosInstance.post(`/hardware/${hardwareId}/audio/input`, { input }),
        {
            onSuccess: () => showToast("success", "Audio", "Input switched!"),
            onError: (err) => showToast("error", "Audio", err.response?.data?.message),
        }
    );
};

// ─── Role-filtered list (device_role: control_box | encoder | decoder | ir_blaster) ──
export const useGetHardwareByRole = (role, extraParams = {}, options = {}) => {
    const { showToast } = useCustomToast();
    return useQuery(
        ["hardwareByRole", role, extraParams],
        async () => (await axiosInstance.get("/hardware", { params: { role, ...extraParams } })).data,
        {
            enabled: !!role && options.enabled !== false,
            keepPreviousData: true,
            onError: (err) => showToast("error", "Hardware", err.response?.data?.message),
        }
    );
};

// ─── Sources panel (Remote Control) ─────────────────────────────────────────
// GET /api/hardware/sources — aggregated Player → Receiver → Encoder rows.
export const useGetSources = (options = {}) => {
    const { showToast } = useCustomToast();
    return useQuery(
        "hardwareSources",
        async () => (await axiosInstance.get("/hardware/sources")).data,
        {
            keepPreviousData: true,
            onError: (err) => showToast("error", "Sources", err.response?.data?.message),
            ...options,
        }
    );
};

// GET /api/hardware/matrix/routing-state — live encoder→screen routing for the
// colour-coded overlay. Backend-tracked (no device round-trip); polled so the
// page reflects switches made elsewhere, and invalidated right after a route.
export const useRoutingState = (options = {}) => {
    return useQuery(
        "matrixRoutingState",
        async () => (await axiosInstance.get("/hardware/matrix/routing-state")).data,
        {
            refetchInterval: 7000,
            staleTime: 3000,
            keepPreviousData: true,
            ...options,
        }
    );
};

// POST /api/hardware/route-source — PTN-only matrix routing, no campaign push.
export const useRouteSource = () => {
    const { showToast } = useCustomToast();
    const qc = useQueryClient();
    return useMutation(
        // options: 'va' (video+audio, default) | 'v' (video only) | 'a' (audio only)
        ({ encoder_id, screen_ids, options = "va" }) =>
            axiosInstance.post("/hardware/route-source", { encoder_id, screen_ids, options }),
        {
            // Refresh the colour overlay immediately after a successful route.
            onSuccess: () => qc.invalidateQueries("matrixRoutingState"),
            onError: (err) => showToast("error", "Sources", err.response?.data?.message),
        }
    );
};

// POST /api/hardware/restore-campaign — bounce a player off HDMI_IN back to
// its prior campaign. Server reads device.previous_campaign_id; 422 if empty.
// Invalidates hardwareSources so the card re-renders with the new state.
export const useRestoreCampaign = () => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    return useMutation(
        ({ device_id }) =>
            axiosInstance.post("/hardware/restore-campaign", { device_id }),
        {
            onSuccess: () => qc.invalidateQueries("hardwareSources"),
            onError: (err) => showToast("error", "Sources", err.response?.data?.message),
        }
    );
};

// POST /api/hardware/switch-player-campaign — switch player to any company
// campaign. Distinct from channel switching: no IR, no matrix, just the
// player-side campaign swap + Firebase ping.
export const useSwitchPlayerCampaign = () => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    const ensureHold = useHoldGuard(); // schedule v4: asks first when this screen is inside a schedule window
    return useMutation(
        async ({ device_id, campaign_id }) => {
            await ensureHold({ mode: 'screens', ids: [device_id] });
            return axiosInstance.post("/hardware/switch-player-campaign", { device_id, campaign_id });
        },
        {
            onSuccess: () => qc.invalidateQueries("hardwareSources"),
            onError: (err) => { if (err?.holdCancelled) return; showToast("error", "Sources", err.response?.data?.message); },
        }
    );
};

// ─── Status / test connection ───────────────────────────────────────────────
// Mutation (not query) so the user can click "Test" on demand.
export const useHardwareStatus = (hardwareId) => {
    const { showToast } = useCustomToast();
    return useMutation(
        () => axiosInstance.get(`/hardware/${hardwareId}/status`).then((r) => r.data),
        {
            onError: (err) => showToast("error", "Hardware", err.response?.data?.message),
        }
    );
};

export const useHardwareLogs = (hardwareId, page = 1) => {
    const { showToast } = useCustomToast();
    return useQuery(
        ["hardwareLogs", hardwareId, page],
        () => axiosInstance.get(`/hardware/${hardwareId}/logs`, { params: { page, per_page: 20 } }).then((r) => r.data),
        {
            enabled: !!hardwareId,
            keepPreviousData: true,
            onError: (err) => showToast("error", "Hardware Logs", err.response?.data?.message),
        }
    );
};

// ─── Available driver types (for the device form's driver dropdown) ──────────
// Backend: GET /hardware/driver-types?type=matrix → ['kramer_vs88h2',
// 'ptn_ips600e', 'ptn_ip5000', ...] (mirrors DriverFactory::driversForType).
export const useDriverTypes = (type) => {
    return useQuery(
        ["driverTypes", type],
        () => axiosInstance.get("/hardware/driver-types", { params: type ? { type } : {} }).then((r) => r.data),
        {
            staleTime: 5 * 60 * 1000,
            // Unwrap the JsonResource {data:[...]} wrapper (resource wrapping is ON).
            select: (res) => (Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []),
        }
    );
};
